"use client";

import {
  CircleUserRound,
  House,
  LayoutDashboard,
  LogOut,
  UserRoundCog,
} from "lucide-react";
import { usePathname, useRouter } from "next/navigation";
import { useEffect, useState, useTransition } from "react";

import { useAuth } from "@/components/providers/auth-provider";
import { SignOutDialog } from "@/components/sign-out-dialog";
import { TextShimmer } from "@/components/text-shimmer";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { absoluteUrl, cn } from "@/lib/utils";

const menuItems = [
  {
    href: "/",
    label: "Home",
    shortcut: "⌘H",
    icon: House,
  },
  {
    href: "/dashboard",
    label: "Dashboard",
    shortcut: "⌘D",
    icon: LayoutDashboard,
  },

  {
    href: "/dashboard/my-profile",
    label: "My Profile",
    shortcut: "⌘P",
    icon: CircleUserRound,
  },
  {
    href: "/dashboard/account",
    label: "Account",
    shortcut: "⌘A",
    icon: UserRoundCog,
  },
] as const;

function getNameInitials(name: string | null | undefined) {
  if (!name) return "U";
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "U";
  const initials = words
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() ?? "");
  return initials.join("");
}

function normalizePath(path: string) {
  if (path.length > 1 && path.endsWith("/")) {
    return path.slice(0, -1);
  }
  return path;
}

export function ProfileDropdown() {
  const router = useRouter();
  const pathname = usePathname();
  const { user, isHydrated } = useAuth();
  const [isSignOutDialogOpen, setIsSignOutDialogOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [activeAction, setActiveAction] = useState<string | null>(null);
  const [isPending, startTransition] = useTransition();

  const userName = typeof user?.name === "string" ? user.name : "";
  const userEmail = typeof user?.email === "string" ? user.email : "";
  const avatarUrl = absoluteUrl(user?.photo || "", true);

  const goToPage = (href: string, label: string) => {
    setActiveAction(label);
    startTransition(() => {
      router.push(href);
    });
  };

  useEffect(() => {
    if (!isPending && activeAction) {
      const closeTimer = window.setTimeout(() => {
        setIsDropdownOpen(false);
        setActiveAction(null);
      }, 0);
      return () => window.clearTimeout(closeTimer);
    }
  }, [activeAction, isPending]);

  return (
    <>
      <DropdownMenu
        modal={false}
        open={isDropdownOpen}
        onOpenChange={(nextOpen) => {
          if (isPending) return;
          setIsDropdownOpen(nextOpen);
          if (!nextOpen) {
            setActiveAction(null);
          }
        }}
      >
        <DropdownMenuTrigger asChild>
          <Avatar className="cursor-pointer ">
            <AvatarImage src={avatarUrl} alt={userName || "User avatar"} />
            <AvatarFallback>{getNameInitials(userName)}</AvatarFallback>
          </Avatar>
        </DropdownMenuTrigger>

        <DropdownMenuContent className="w-60" align="end" forceMount>
          <DropdownMenuLabel className="font-normal">
            <div className="flex flex-col gap-1.5">
              <p className="text-sm leading-none font-medium">
                {isHydrated ? userName || "User" : "Loading..."}
              </p>
              <p className="text-muted-foreground text-xs leading-none">
                {isHydrated ? userEmail || "No email" : "Loading account"}
              </p>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuGroup>
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isCurrentPath =
                normalizePath(pathname) === normalizePath(item.href);
              return (
                <DropdownMenuItem
                  key={item.label}
                  disabled={isPending}
                  onSelect={(event) => {
                    event.preventDefault();
                    goToPage(item.href, item.label);
                  }}
                  className={cn(
                    "flex cursor-pointer items-center justify-between",
                    isCurrentPath && "bg-primary/10 text-primary font-medium",
                  )}
                >
                  <span className="flex items-center gap-2">
                    <Icon className="size-4" />
                    {activeAction === item.label && isPending ? (
                      <TextShimmer as="span" duration={1.2}>
                        {item.label}
                      </TextShimmer>
                    ) : (
                      item.label
                    )}
                  </span>
                  <DropdownMenuShortcut
                    className={cn(isCurrentPath && "text-primary")}
                  >
                    {item.shortcut}
                  </DropdownMenuShortcut>
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuGroup>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="cursor-pointer"
            variant="destructive"
            disabled={isPending}
            onSelect={(event) => {
              event.preventDefault();
              setIsSignOutDialogOpen(true);
            }}
          >
            <span className="flex items-center gap-2">
              <LogOut className="size-4" />
              Sign out
            </span>
            <DropdownMenuShortcut className="text-current">
              ⇧⌘Q
            </DropdownMenuShortcut>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <SignOutDialog
        open={isSignOutDialogOpen}
        onOpenChange={setIsSignOutDialogOpen}
      />
    </>
  );
}
